import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Search, Filter, UserCheck, UserX, Edit, Trash2, Eye, Users } from 'lucide-react';
import { AppDispatch, RootState } from '@/store';
import { PermissionButton, PermissionGuard } from '@/components/auth';
import { Permission } from '@/utils/permissions';
import {
  fetchUsers,
  fetchUserRoles,
  activateUser,
  deactivateUser,
  deleteUser,
  setFilters,
  clearError
} from '@/store/slices/usersSlice';
import { UserRole, User } from '@/types';
import { DataTable } from '@/components/ui/DataTable';
import {
  GlassCard,
  GlassButton,
  GlassInput,
  GlassSelect,
  GlassBadge,
  GlassModal,
  GlassPageHeader,
  GlassAlert,
  GlassLoadingSpinner,
  SectionReveal,
} from '@/design-system';
import { UserForm } from './components/UserForm';
import { UserDetailModal } from './components/UserDetailModal';

export const UsersPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    users, 
    roles, 
    isLoading, 
    error, 
    pagination, 
    filters 
  } = useSelector((state: RootState) => state.users);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    dispatch(fetchUsers(filters));
    dispatch(fetchUserRoles());
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleSearch = () => {
    dispatch(setFilters({ ...filters, search: searchTerm, page: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, page: 1 };
    if (value !== undefined && value !== '') {
      newFilters[key] = value;
    } else {
      delete newFilters[key];
    }
    dispatch(setFilters(newFilters));
  };

  const handlePageChange = (page: number) => {
    dispatch(fetchUsers({ ...filters, page }));
  };

  const handleActivateUser = async (user: User) => {
    if (window.confirm(`Are you sure you want to activate ${user.firstName} ${user.lastName}?`)) {
      await dispatch(activateUser(user.id));
      dispatch(fetchUsers(filters));
    }
  };

  const handleDeactivateUser = async (user: User) => {
    if (window.confirm(`Are you sure you want to deactivate ${user.firstName} ${user.lastName}?`)) {
      await dispatch(deactivateUser(user.id));
      dispatch(fetchUsers(filters));
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (window.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`)) {
      await dispatch(deleteUser(user.id));
      dispatch(fetchUsers(filters));
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    dispatch(fetchUsers(filters));
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    dispatch(fetchUsers(filters));
  };

  const formatRoleLabel = (role: UserRole) => {
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (value: any, user: User) => (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">
            {user.firstName} {user.lastName}
          </span>
          <span className="text-sm text-muted-foreground/60">{user.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (value: UserRole) => (
        <GlassBadge
          variant={
            value === UserRole.EXECUTIVE ? 'success' :
            value === UserRole.PMO ? 'info' :
            value === UserRole.IS_OWNER ? 'success' :
            value === UserRole.PROGRAM_LEAD ? 'warning' :
            value === UserRole.FINANCE ? 'warning' : 'neutral'
          }
        >
          {formatRoleLabel(value)}
        </GlassBadge>
      ),
    },
    {
      key: 'isActive',
      header: 'Status',
      sortable: true,
      render: (value: boolean) => (
        <GlassBadge variant={value ? 'success' : 'danger'}>
          {value ? 'Active' : 'Inactive'}
        </GlassBadge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (value: any, user: User) => (
        <div className="flex items-center space-x-2">
          <GlassButton
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedUser(user);
              setShowDetailModal(true);
            }}
            className="text-accent-blue hover:text-accent-blue-hover"
          >
            <Eye className="h-4 w-4" />
          </GlassButton>
          <PermissionGuard permission={Permission.MANAGE_USERS}>
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedUser(user);
                setShowEditModal(true);
              }}
              className="text-muted-foreground hover:text-foreground"
            >
              <Edit className="h-4 w-4" />
            </GlassButton>
            {user.isActive ? (
              <GlassButton
                variant="ghost"
                size="sm"
                onClick={() => handleDeactivateUser(user)}
                className="text-accent-amber hover:text-accent-amber"
              >
                <UserX className="h-4 w-4" />
              </GlassButton>
            ) : (
              <GlassButton
                variant="ghost"
                size="sm"
                onClick={() => handleActivateUser(user)}
                className="text-accent-emerald hover:text-accent-emerald"
              >
                <UserCheck className="h-4 w-4" />
              </GlassButton>
            )}
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteUser(user)}
              className="text-accent-rose hover:text-accent-rose"
            >
              <Trash2 className="h-4 w-4" />
            </GlassButton>
          </PermissionGuard>
        </div>
      ),
    },
  ];

  if (isLoading && users.length === 0) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading users..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Header */}
        <SectionReveal>
          <GlassPageHeader
            title="User Management"
            subtitle="Manage user accounts and permissions"
            icon={<Users className="w-6 h-6" />}
            iconGradient="blue-violet"
            className="mb-6"
            actions={
              <PermissionButton
                permission={Permission.MANAGE_USERS}
                onClick={() => setShowCreateModal(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Add User
              </PermissionButton>
            }
          />
        </SectionReveal>

        {/* Error Message */}
        {error && (
          <SectionReveal delay={0.1}>
            <GlassAlert variant="error" message={error} />
          </SectionReveal>
        )}

        {/* Search and Filters */}
        <SectionReveal delay={0.15}>
          <GlassCard padding="md">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <GlassInput
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(value) => setSearchTerm(value)}
                      leftIcon={<Search className="h-4 w-4" />}
                    />
                  </div>
                  <GlassButton onClick={handleSearch} variant="secondary">
                    Search
                  </GlassButton>
                </div>
              </div>
              <GlassButton
                variant="secondary"
                onClick={() => setShowFilters(!showFilters)}
                leftIcon={<Filter className="h-4 w-4" />}
              >
                Filters
              </GlassButton>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-border-primary">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <GlassSelect
                    label="Role"
                    value={filters.role || ''}
                    onChange={(value) => handleFilterChange('role', value || undefined)}
                    options={[
                      { value: '', label: 'All Roles' },
                      ...roles.map(role => ({ value: role.value, label: role.label }))
                    ]}
                  />
                  <GlassSelect
                    label="Status"
                    value={filters.isActive !== undefined ? filters.isActive.toString() : ''}
                    onChange={(value) => handleFilterChange('isActive', value === '' ? undefined : value === 'true')}
                    options={[
                      { value: '', label: 'All Statuses' },
                      { value: 'true', label: 'Active' },
                      { value: 'false', label: 'Inactive' },
                    ]}
                  />
                </div>
              </div>
            )}
          </GlassCard>
        </SectionReveal>

      {/* Users Table */}
      <SectionReveal delay={0.2}>
        <GlassCard padding="none">
          <div className="max-h-[calc(100vh-350px)] overflow-auto">
            <DataTable
              data={users}
              columns={columns}
              isLoading={isLoading}
              pagination={{
                page: pagination.page,
                limit: pagination.limit,
                total: pagination.total,
                pages: pagination.pages,
                onPageChange: handlePageChange,
              }}
            />
          </div>
        </GlassCard>
      </SectionReveal>

        {/* Modals */}
        <GlassModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Add New User"
          subtitle="Create a new user account"
          icon={<Plus className="w-5 h-5" />}
          iconGradient="emerald"
          size="md"
        >
          <UserForm
            onSuccess={handleCreateSuccess}
            onCancel={() => setShowCreateModal(false)}
          />
        </GlassModal>

        <GlassModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          title="Edit User"
          subtitle="Update user information"
          icon={<Edit className="w-5 h-5" />}
          iconGradient="blue-violet"
          size="md"
        >
          {selectedUser && (
            <UserForm
              user={selectedUser}
              onSuccess={handleEditSuccess}
              onCancel={() => {
                setShowEditModal(false);
                setSelectedUser(null);
              }}
            />
          )}
        </GlassModal>

        <GlassModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedUser(null);
          }}
          title="User Details"
          subtitle="View user information and permissions"
          icon={<Eye className="w-5 h-5" />}
          iconGradient="cyan"
          size="lg"
        >
          {selectedUser && (
            <UserDetailModal
              user={selectedUser}
              onClose={() => {
                setShowDetailModal(false);
                setSelectedUser(null);
              }}
            />
          )}
        </GlassModal>
      </div>
    </div>
  );
};